/* Brand layer: scroll reveal, accessible autoplay-video toggle, marquee pause for reduced motion, product gallery, product reviews. */
(() => {
  document.documentElement.classList.add('b-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let observer;
  const reveal = (root = document) => {
    const items = root.querySelectorAll('[data-b-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window) || reduceMotion.matches) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    observer ??= new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
  };

  const setupVideos = (root = document) => {
    root.querySelectorAll('video[data-b-autoplay]').forEach((video) => {
      const toggle = video.parentElement.querySelector('[data-b-video-toggle]');
      const sync = () => {
        if (!toggle) return;
        toggle.setAttribute('aria-pressed', String(video.paused));
        toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
      };
      if (reduceMotion.matches) video.pause();
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      toggle?.addEventListener('click', () => (video.paused ? video.play() : video.pause()));
      sync();
    });
  };

  const init = (root) => {
    reveal(root);
    setupVideos(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /* Product gallery (snippets/brand-gallery.liquid): swipe, arrows, thumbnails, keyboard, zoom dialog. */
  if (!customElements.get('b-gallery')) {
    customElements.define(
      'b-gallery',
      class extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-b-track]');
          this.slides = [...this.querySelectorAll('[data-b-slide]')];
          this.thumbs = [...this.querySelectorAll('[data-b-thumb]')];
          this.thumbRow = this.querySelector('[data-b-thumbs]');
          this.dialog = this.querySelector('dialog');
          this.index = 0;
          this.addEventListener('click', this.onClick);
          this.track?.addEventListener('keydown', this.onKey);
          document.addEventListener('variant:update', this.onVariant);
          if ('IntersectionObserver' in window && this.slides.length > 1) {
            this.slideObserver = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
                });
              },
              { root: this.track, threshold: 0.6 }
            );
            this.slides.forEach((slide) => this.slideObserver.observe(slide));
          }
        }

        disconnectedCallback() {
          this.slideObserver?.disconnect();
          this.removeEventListener('click', this.onClick);
          document.removeEventListener('variant:update', this.onVariant);
        }

        go(index) {
          const count = this.slides.length;
          if (!count || !this.track) return;
          const next = (index + count) % count;
          this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          this.setActive(next);
        }

        setActive(index) {
          if (index < 0) return;
          this.index = index;
          this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
          const thumb = this.thumbs[index];
          if (!thumb || !this.thumbRow || this.thumbRow.scrollWidth <= this.thumbRow.clientWidth) return;
          this.thumbRow.scrollTo({
            left: thumb.offsetLeft - (this.thumbRow.clientWidth - thumb.clientWidth) / 2,
            behavior: reduceMotion.matches ? 'auto' : 'smooth',
          });
        }

        onClick = (event) => {
          const thumb = event.target.closest('[data-b-thumb]');
          if (thumb) return this.go(Number(thumb.dataset.bThumb));
          const nav = event.target.closest('[data-b-nav]');
          if (nav) return this.go(this.index + Number(nav.dataset.bNav));
          const zoom = event.target.closest('[data-b-zoom]');
          if (zoom && this.dialog?.showModal) {
            const image = this.dialog.querySelector('img');
            image.src = zoom.dataset.src;
            image.alt = zoom.dataset.alt || '';
            this.dialog.showModal();
            return;
          }
          if (event.target.closest('[data-b-close]') || event.target === this.dialog) this.dialog?.close();
        };

        onKey = (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        };

        onVariant = (event) => {
          const id = event.detail?.resource?.featured_media?.id;
          const index = id ? this.slides.findIndex((slide) => slide.dataset.mediaId === String(id)) : -1;
          if (index > -1) this.go(index);
        };
      }
    );
  }

  /* Product reviews (sections/brand-reviews.liquid): full review pop-up, verification info toggle, show more. */
  if (!customElements.get('b-reviews')) {
    customElements.define(
      'b-reviews',
      class extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          this.reviewSlot = this.querySelector('[data-b-review-slot]');
          this.addEventListener('click', this.onClick);
        }

        disconnectedCallback() {
          this.removeEventListener('click', this.onClick);
        }

        onClick = (event) => {
          const card = event.target.closest('[data-b-review]');
          if (card) {
            const template = card.closest('li')?.querySelector('template');
            if (!template || !this.reviewSlot || !this.dialog?.showModal) return;
            this.reviewSlot.replaceChildren(template.content.cloneNode(true));
            this.dialog.showModal();
            return;
          }
          const info = event.target.closest('[data-b-info]');
          if (info) {
            const panel = info.closest('.b-rv__full')?.querySelector('[data-b-info-panel]');
            const open = info.getAttribute('aria-expanded') !== 'true';
            info.setAttribute('aria-expanded', String(open));
            if (panel) panel.hidden = !open;
            return;
          }
          const more = event.target.closest('[data-b-more]');
          if (more) {
            const extras = this.querySelectorAll('[data-b-extra]');
            extras.forEach((item) => (item.hidden = false));
            extras[0]?.querySelector('button')?.focus();
            more.parentElement.remove();
            return;
          }
          if (event.target.closest('[data-b-close]') || event.target === this.dialog) this.dialog?.close();
        };
      }
    );
  }
})();
